import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import '../styles/Reports.css';

const SalesReport = () => {
  const [reportType, setReportType] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sales, setSales] = useState([]);
  const [monthlySales, setMonthlySales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');
  const [summary, setSummary] = useState({
    totalSales: 0,
    totalGST: 0,
    totalWithGST: 0,
    totalExcludingGST: 0,
    ordersCount: 0,
    cashSales: 0,
    transferSales: 0,
    cardSales: 0,
    cashCount: 0,
    transferCount: 0,
    cardCount: 0
  });

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Company information for print reports
  const companyInfo = {
    name: 'Your Company Name',
    phone: '+960 123-4567',
    gst: 'GST123456789',
    tin: 'TIN987654321',
    address: 'Male\', Maldives'
  };

  useEffect(() => {
    fetchSalesData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, selectedDate, selectedMonth, selectedYear, startDate, endDate]);

  const fetchSalesData = async () => {
    if (reportType === 'custom' && (!startDate || !endDate)) {
      return;
    }

    setLoading(true);
    try {
      let start, end;

      switch (reportType) {
        case 'daily':
          start = new Date(selectedDate);
          start.setHours(0, 0, 0, 0);
          end = new Date(selectedDate);
          end.setHours(23, 59, 59, 999);
          break;

        case 'monthly':
          const [year, month] = selectedMonth.split('-');
          start = new Date(parseInt(year), parseInt(month) - 1, 1);
          end = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59, 999);
          break;

        case 'yearly':
          start = new Date(selectedYear, 0, 1);
          end = new Date(selectedYear, 11, 31, 23, 59, 59, 999);
          break;

        case 'custom':
          start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          break;

        default:
          return;
      }

      const salesQuery = query(
        collection(db, 'sales'),
        where('date', '>=', start.toISOString()),
        where('date', '<=', end.toISOString())
      );

      const salesSnapshot = await getDocs(salesQuery);
      const salesList = salesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setSales(salesList);

      if (reportType === 'yearly') {
        const monthlyData = processMonthlyData(salesList);
        setMonthlySales(monthlyData);
      }

      calculateSummary(salesList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching sales:', error);
      setLoading(false);
    }
  };

  const processMonthlyData = (salesData) => {
    const monthlyStats = Array(12).fill(null).map((_, index) => ({
      month: monthNames[index],
      monthNumber: index,
      subtotal: 0,
      gst: 0,
      totalWithGST: 0,
      ordersCount: 0
    }));

    salesData.forEach(sale => {
      const saleDate = new Date(sale.date);
      const monthIndex = saleDate.getMonth();

      monthlyStats[monthIndex].subtotal += sale.subtotal;
      monthlyStats[monthIndex].gst += sale.gst;
      monthlyStats[monthIndex].totalWithGST += sale.totalWithGST;
      monthlyStats[monthIndex].ordersCount += 1;
    });

    return monthlyStats;
  };

  const calculateSummary = (salesData) => {
    const totalExcludingGST = salesData.reduce((sum, sale) => sum + sale.subtotal, 0);
    const totalGST = salesData.reduce((sum, sale) => sum + sale.gst, 0);
    const totalWithGST = salesData.reduce((sum, sale) => sum + sale.totalWithGST, 0);

    // Calculate payment type breakdown
    const cashSales = salesData.filter(s => s.paymentType === 'cash').reduce((sum, sale) => sum + sale.totalWithGST, 0);
    const transferSales = salesData.filter(s => s.paymentType === 'transfer').reduce((sum, sale) => sum + sale.totalWithGST, 0);
    const cardSales = salesData.filter(s => s.paymentType === 'card').reduce((sum, sale) => sum + sale.totalWithGST, 0);

    const cashCount = salesData.filter(s => s.paymentType === 'cash').length;
    const transferCount = salesData.filter(s => s.paymentType === 'transfer').length;
    const cardCount = salesData.filter(s => s.paymentType === 'card').length;

    setSummary({
      totalSales: totalWithGST,
      totalGST: totalGST,
      totalWithGST: totalWithGST,
      totalExcludingGST: totalExcludingGST,
      ordersCount: salesData.length,
      cashSales,
      transferSales,
      cardSales,
      cashCount,
      transferCount,
      cardCount
    });
  };

  const getReportTitle = () => {
    switch (reportType) {
      case 'daily':
        return `Daily Sales Report - ${new Date(selectedDate).toLocaleDateString()}`;
      case 'monthly':
        const [year, month] = selectedMonth.split('-');
        return `Monthly Sales Report - ${monthNames[parseInt(month) - 1]} ${year}`;
      case 'yearly':
        return `Yearly Sales Report - ${selectedYear}`;
      case 'custom':
        return `Sales Report - ${startDate ? new Date(startDate).toLocaleDateString() : ''} to ${endDate ? new Date(endDate).toLocaleDateString() : ''}`;
      default:
        return 'Sales Report';
    }
  };

  const getFilteredSales = () => {
    if (paymentTypeFilter === 'all') return sales;
    return sales.filter(sale => sale.paymentType === paymentTypeFilter);
  };

  const exportToCSV = () => {
    let csvContent = `${getReportTitle()}\n\n`;
    const filteredSales = getFilteredSales();

    if (reportType === 'yearly') {
      const headers = ['Month', 'Orders', 'Sales (Excl. GST)', 'GST (8%)', 'Total (Incl. GST)'];
      csvContent += headers.join(',') + '\n';

      monthlySales.forEach(month => {
        csvContent += [
          month.month,
          month.ordersCount,
          month.subtotal.toFixed(2),
          month.gst.toFixed(2),
          month.totalWithGST.toFixed(2)
        ].join(',') + '\n';
      });
    } else {
      const headers = ['Order ID', 'Date/Time', 'Payment Type', 'Items', 'Subtotal', 'GST (8%)', 'Total with GST'];
      csvContent += headers.join(',') + '\n';

      filteredSales.forEach(sale => {
        csvContent += [
          `#${sale.orderNumber || sale.orderId.substring(0, 8)}`,
          new Date(sale.date).toLocaleString(),
          sale.paymentType || 'N/A',
          sale.items.map(item => `${item.name} (x${item.quantity})`).join('; '),
          sale.subtotal.toFixed(2),
          sale.gst.toFixed(2),
          sale.totalWithGST.toFixed(2)
        ].join(',') + '\n';
      });
    }

    csvContent += '\n';
    csvContent += `Total Orders,${summary.ordersCount}\n`;
    csvContent += `Total Sales (Excl. GST),${summary.totalExcludingGST.toFixed(2)}\n`;
    csvContent += `Total GST (8%),${summary.totalGST.toFixed(2)}\n`;
    csvContent += `Total Sales (Incl. GST),${summary.totalWithGST.toFixed(2)}\n`;
    csvContent += '\n';
    csvContent += `Cash Sales (${summary.cashCount} orders),${summary.cashSales.toFixed(2)}\n`;
    csvContent += `Transfer Sales (${summary.transferCount} orders),${summary.transferSales.toFixed(2)}\n`;
    csvContent += `Card Sales (${summary.cardCount} orders),${summary.cardSales.toFixed(2)}\n`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-report-${reportType}-${Date.now()}.csv`;
    a.click();
  };

  const printReport = () => {
    const filteredSales = getFilteredSales();
    const printWindow = window.open('', '_blank');

    let reportContent = '';

    if (reportType === 'yearly') {
      reportContent = `
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
          <thead>
            <tr style="background-color: #f8f9fa; border-bottom: 2px solid #000;">
              <th style="padding: 12px; text-align: left; border: 1px solid #dee2e6;">Month</th>
              <th style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">Orders</th>
              <th style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">Sales (Excl. GST)</th>
              <th style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">GST (8%)</th>
              <th style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">Total (Incl. GST)</th>
            </tr>
          </thead>
          <tbody>
            ${monthlySales.map(month => `
              <tr style="border-bottom: 1px solid #dee2e6;">
                <td style="padding: 10px; border: 1px solid #dee2e6;">${month.month}</td>
                <td style="padding: 10px; text-align: right; border: 1px solid #dee2e6;">${month.ordersCount}</td>
                <td style="padding: 10px; text-align: right; border: 1px solid #dee2e6;">MVR ${month.subtotal.toFixed(2)}</td>
                <td style="padding: 10px; text-align: right; border: 1px solid #dee2e6;">MVR ${month.gst.toFixed(2)}</td>
                <td style="padding: 10px; text-align: right; border: 1px solid #dee2e6;">MVR ${month.totalWithGST.toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
          <tfoot>
            <tr style="background-color: #f8f9fa; font-weight: bold; border-top: 2px solid #000;">
              <td style="padding: 12px; border: 1px solid #dee2e6;">TOTAL</td>
              <td style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">${summary.ordersCount}</td>
              <td style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">MVR ${summary.totalExcludingGST.toFixed(2)}</td>
              <td style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">MVR ${summary.totalGST.toFixed(2)}</td>
              <td style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">MVR ${summary.totalWithGST.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      `;
    } else {
      reportContent = `
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
          <thead>
            <tr style="background-color: #f8f9fa; border-bottom: 2px solid #000;">
              <th style="padding: 12px; text-align: left; border: 1px solid #dee2e6;">Order ID</th>
              <th style="padding: 12px; text-align: left; border: 1px solid #dee2e6;">Date/Time</th>
              <th style="padding: 12px; text-align: left; border: 1px solid #dee2e6;">Payment</th>
              <th style="padding: 12px; text-align: left; border: 1px solid #dee2e6;">Items</th>
              <th style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">Subtotal</th>
              <th style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">GST (8%)</th>
              <th style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${filteredSales.map(sale => `
              <tr style="border-bottom: 1px solid #dee2e6;">
                <td style="padding: 10px; border: 1px solid #dee2e6;">#${sale.orderNumber || sale.orderId.substring(0, 8)}</td>
                <td style="padding: 10px; border: 1px solid #dee2e6; font-size: 11px;">${new Date(sale.date).toLocaleString()}</td>
                <td style="padding: 10px; border: 1px solid #dee2e6;">${sale.paymentType || 'N/A'}</td>
                <td style="padding: 10px; border: 1px solid #dee2e6; font-size: 11px;">
                  ${sale.items.map(item => `${item.name} (x${item.quantity})`).join('<br>')}
                </td>
                <td style="padding: 10px; text-align: right; border: 1px solid #dee2e6;">MVR ${sale.subtotal.toFixed(2)}</td>
                <td style="padding: 10px; text-align: right; border: 1px solid #dee2e6;">MVR ${sale.gst.toFixed(2)}</td>
                <td style="padding: 10px; text-align: right; border: 1px solid #dee2e6;">MVR ${sale.totalWithGST.toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
          <tfoot>
            <tr style="background-color: #f8f9fa; font-weight: bold; border-top: 2px solid #000;">
              <td colspan="4" style="padding: 12px; border: 1px solid #dee2e6;">TOTAL</td>
              <td style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">MVR ${summary.totalExcludingGST.toFixed(2)}</td>
              <td style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">MVR ${summary.totalGST.toFixed(2)}</td>
              <td style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">MVR ${summary.totalWithGST.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      `;
    }

    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${getReportTitle()}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Arial', sans-serif;
            padding: 20px;
            color: #000;
            font-size: 12px;
          }
          .print-container {
            max-width: 100%;
            margin: 0 auto;
          }
          .company-header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px double #000;
          }
          .company-name {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 10px;
            text-transform: uppercase;
          }
          .company-details {
            font-size: 11px;
            line-height: 1.6;
            color: #333;
          }
          .company-details div {
            margin: 3px 0;
          }
          .report-title {
            text-align: center;
            font-size: 18px;
            font-weight: bold;
            margin: 20px 0;
            text-transform: uppercase;
          }
          .report-metadata {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
            padding: 10px;
            background-color: #f8f9fa;
            border: 1px solid #dee2e6;
          }
          .summary-section {
            margin: 20px 0;
            padding: 15px;
            background-color: #f8f9fa;
            border: 2px solid #000;
          }
          .summary-section h3 {
            margin-bottom: 10px;
            font-size: 14px;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
            margin-top: 10px;
          }
          .summary-item {
            display: flex;
            justify-content: space-between;
            padding: 5px;
            border-bottom: 1px solid #dee2e6;
          }
          .summary-item strong {
            font-weight: bold;
          }
          .payment-breakdown {
            margin: 20px 0;
            padding: 15px;
            background-color: #fff;
            border: 1px solid #000;
          }
          .payment-breakdown h3 {
            margin-bottom: 10px;
            font-size: 14px;
          }
          .payment-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
            margin-top: 10px;
          }
          .payment-item {
            padding: 10px;
            border: 1px solid #dee2e6;
            text-align: center;
          }
          .payment-label {
            font-size: 11px;
            color: #666;
            margin-bottom: 5px;
          }
          .payment-value {
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 3px;
          }
          .payment-count {
            font-size: 10px;
            color: #666;
          }
          .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 2px solid #000;
            text-align: center;
            font-size: 11px;
            color: #666;
          }
          @media print {
            body { padding: 10px; }
            .report-metadata { break-inside: avoid; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          <!-- Company Header -->
          <div class="company-header">
            <div class="company-name">${companyInfo.name}</div>
            <div class="company-details">
              <div><strong>Phone:</strong> ${companyInfo.phone}</div>
              <div><strong>GST No:</strong> ${companyInfo.gst}</div>
              <div><strong>TIN:</strong> ${companyInfo.tin}</div>
              <div><strong>Address:</strong> ${companyInfo.address}</div>
            </div>
          </div>

          <!-- Report Title -->
          <div class="report-title">${getReportTitle()}</div>

          <!-- Report Metadata -->
          <div class="report-metadata">
            <div><strong>Generated:</strong> ${new Date().toLocaleString()}</div>
            <div><strong>Report Type:</strong> ${reportType.charAt(0).toUpperCase() + reportType.slice(1)}</div>
          </div>

          <!-- Summary Section -->
          <div class="summary-section">
            <h3>Sales Summary</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <span>Total Orders:</span>
                <strong>${summary.ordersCount}</strong>
              </div>
              <div class="summary-item">
                <span>Sales (Excl. GST):</span>
                <strong>MVR ${summary.totalExcludingGST.toFixed(2)}</strong>
              </div>
              <div class="summary-item">
                <span>Total GST (8%):</span>
                <strong>MVR ${summary.totalGST.toFixed(2)}</strong>
              </div>
              <div class="summary-item">
                <span>Total Sales (Incl. GST):</span>
                <strong>MVR ${summary.totalWithGST.toFixed(2)}</strong>
              </div>
            </div>
          </div>

          <!-- Payment Breakdown -->
          <div class="payment-breakdown">
            <h3>Payment Type Breakdown</h3>
            <div class="payment-grid">
              <div class="payment-item">
                <div class="payment-label">Cash Sales</div>
                <div class="payment-value">MVR ${summary.cashSales.toFixed(2)}</div>
                <div class="payment-count">${summary.cashCount} orders</div>
              </div>
              <div class="payment-item">
                <div class="payment-label">Transfer Sales</div>
                <div class="payment-value">MVR ${summary.transferSales.toFixed(2)}</div>
                <div class="payment-count">${summary.transferCount} orders</div>
              </div>
              <div class="payment-item">
                <div class="payment-label">Card Sales</div>
                <div class="payment-value">MVR ${summary.cardSales.toFixed(2)}</div>
                <div class="payment-count">${summary.cardCount} orders</div>
              </div>
            </div>
          </div>

          <!-- Sales Table -->
          ${reportContent}

          <!-- Footer -->
          <div class="footer">
            <div>This is a computer-generated report</div>
            <div>${companyInfo.name} - ${new Date().getFullYear()}</div>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printHTML);
    printWindow.document.close();
    printWindow.onload = function() {
      printWindow.focus();
      printWindow.print();
    };
  };

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  const renderDateControls = () => {
    switch (reportType) {
      case 'daily':
        return (
          <div className="date-selector">
            <label>Select Date:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
        );

      case 'monthly':
        return (
          <div className="date-selector">
            <label>Select Month:</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              max={new Date().toISOString().slice(0, 7)}
            />
          </div>
        );

      case 'yearly':
        return (
          <div className="year-selector">
            <label>Select Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        );

      case 'custom':
        return (
          <div className="date-range-selector">
            <div className="date-input">
              <label>Start Date:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="date-input">
              <label>End Date:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return <div className="loading">Loading report...</div>;
  }

  return (
    <div className="sales-report">
      <h1>Sales Report</h1>

      <div className="report-type-selector">
        <label>Report Type:</label>
        <div className="report-type-buttons">
          <button
            className={reportType === 'daily' ? 'active' : ''}
            onClick={() => setReportType('daily')}
          >
            Daily
          </button>
          <button
            className={reportType === 'monthly' ? 'active' : ''}
            onClick={() => setReportType('monthly')}
          >
            Monthly
          </button>
          <button
            className={reportType === 'yearly' ? 'active' : ''}
            onClick={() => setReportType('yearly')}
          >
            Yearly
          </button>
          <button
            className={reportType === 'custom' ? 'active' : ''}
            onClick={() => setReportType('custom')}
          >
            Custom Range
          </button>
        </div>
      </div>

      <div className="report-controls">
        {renderDateControls()}
        <div className="report-actions">
          <button onClick={exportToCSV} className="export-btn" disabled={sales.length === 0 && monthlySales.length === 0}>
            Export to CSV
          </button>
          <button onClick={printReport} className="print-btn">
            Print Report
          </button>
        </div>
      </div>

      <h2 className="report-title">{getReportTitle()}</h2>

      <div className="summary-cards">
        <div className="summary-card">
          <h3>Total Orders</h3>
          <p className="summary-value">{summary.ordersCount}</p>
        </div>
        <div className="summary-card">
          <h3>Sales (Excl. GST)</h3>
          <p className="summary-value">MVR {summary.totalExcludingGST.toFixed(2)}</p>
        </div>
        <div className="summary-card">
          <h3>Total GST (8%)</h3>
          <p className="summary-value gst">MVR {summary.totalGST.toFixed(2)}</p>
        </div>
        <div className="summary-card highlight">
          <h3>Total Sales (Incl. GST)</h3>
          <p className="summary-value">MVR {summary.totalWithGST.toFixed(2)}</p>
        </div>
      </div>

      <div className="payment-breakdown-cards">
        <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Payment Type Breakdown</h3>
        <div className="payment-cards-grid">
          <div className="payment-card cash">
            <div className="payment-icon">💵</div>
            <h4>Cash</h4>
            <p className="payment-amount">MVR {summary.cashSales.toFixed(2)}</p>
            <p className="payment-count">{summary.cashCount} orders</p>
          </div>
          <div className="payment-card transfer">
            <div className="payment-icon">🏦</div>
            <h4>Transfer</h4>
            <p className="payment-amount">MVR {summary.transferSales.toFixed(2)}</p>
            <p className="payment-count">{summary.transferCount} orders</p>
          </div>
          <div className="payment-card card">
            <div className="payment-icon">💳</div>
            <h4>Card</h4>
            <p className="payment-amount">MVR {summary.cardSales.toFixed(2)}</p>
            <p className="payment-count">{summary.cardCount} orders</p>
          </div>
        </div>
      </div>

      <div className="sales-table-container">
        <div className="table-header-with-filter">
          <h2>{reportType === 'yearly' ? 'Monthly Breakdown' : 'Sales Details'}</h2>
          {reportType !== 'yearly' && (
            <div className="payment-type-filter">
              <label>Filter by Payment Type:</label>
              <div className="filter-buttons">
                <button
                  className={paymentTypeFilter === 'all' ? 'active' : ''}
                  onClick={() => setPaymentTypeFilter('all')}
                >
                  All
                </button>
                <button
                  className={paymentTypeFilter === 'cash' ? 'active' : ''}
                  onClick={() => setPaymentTypeFilter('cash')}
                >
                  💵 Cash
                </button>
                <button
                  className={paymentTypeFilter === 'transfer' ? 'active' : ''}
                  onClick={() => setPaymentTypeFilter('transfer')}
                >
                  🏦 Transfer
                </button>
                <button
                  className={paymentTypeFilter === 'card' ? 'active' : ''}
                  onClick={() => setPaymentTypeFilter('card')}
                >
                  💳 Card
                </button>
              </div>
            </div>
          )}
        </div>

        {reportType === 'custom' && (!startDate || !endDate) ? (
          <p className="no-data">Please select both start and end dates</p>
        ) : sales.length === 0 && monthlySales.length === 0 ? (
          <p className="no-data">No sales data for this period</p>
        ) : reportType === 'yearly' ? (
          <table className="sales-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Orders</th>
                <th>Sales (Excl. GST)</th>
                <th>GST (8%)</th>
                <th>Total (Incl. GST)</th>
              </tr>
            </thead>
            <tbody>
              {monthlySales.map((month, index) => (
                <tr key={index} className={month.ordersCount === 0 ? 'no-data-row' : ''}>
                  <td>{month.month}</td>
                  <td>{month.ordersCount}</td>
                  <td>MVR {month.subtotal.toFixed(2)}</td>
                  <td>MVR {month.gst.toFixed(2)}</td>
                  <td>MVR {month.totalWithGST.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td><strong>TOTAL</strong></td>
                <td><strong>{summary.ordersCount}</strong></td>
                <td><strong>MVR {summary.totalExcludingGST.toFixed(2)}</strong></td>
                <td><strong>MVR {summary.totalGST.toFixed(2)}</strong></td>
                <td><strong>MVR {summary.totalWithGST.toFixed(2)}</strong></td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <table className="sales-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Date/Time</th>
                <th>Payment Type</th>
                <th>Items</th>
                <th>Subtotal</th>
                <th>GST (8%)</th>
                <th>Total with GST</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredSales().map(sale => (
                <tr key={sale.id}>
                  <td>#{sale.orderNumber || sale.orderId.substring(0, 8)}</td>
                  <td>{new Date(sale.date).toLocaleString()}</td>
                  <td>
                    <span className={`payment-badge ${sale.paymentType || 'unknown'}`}>
                      {sale.paymentType === 'cash' && '💵 Cash'}
                      {sale.paymentType === 'transfer' && '🏦 Transfer'}
                      {sale.paymentType === 'card' && '💳 Card'}
                      {!sale.paymentType && 'N/A'}
                    </span>
                  </td>
                  <td>
                    {sale.items.map((item, idx) => (
                      <div key={idx}>
                        {item.name} (x{item.quantity})
                      </div>
                    ))}
                  </td>
                  <td>MVR {sale.subtotal.toFixed(2)}</td>
                  <td>MVR {sale.gst.toFixed(2)}</td>
                  <td>MVR {sale.totalWithGST.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan="4"><strong>TOTAL</strong></td>
                <td><strong>MVR {summary.totalExcludingGST.toFixed(2)}</strong></td>
                <td><strong>MVR {summary.totalGST.toFixed(2)}</strong></td>
                <td><strong>MVR {summary.totalWithGST.toFixed(2)}</strong></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
};

export default SalesReport;
